import React, { useState } from "react";
import ProductForm from "./ProductForm";
import Modal from "../../shared/Modal";
import DeleteConfirmationModal from "../../shared/DeleteConfirmationModal";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useMutation, useQueryClient } from "react-query";
import { editProduct, deleteProduct } from "../../../api";
import queryKeys from "../../../api/queryKeys";
import { Box, Button } from "@mui/material";

const EditProductModal = ({ onClose, open, productVariants }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const { mutate: handleEdit, isLoading: isEditing } = useMutation(
    ({ shared, langValues, productVariants: variants }) => {
      const requests = (variants || []).map((variant) => {
        const lang = variant.language;
        const langFields = langValues[lang] || {};
        return editProduct({
          id: variant.id,
          language: lang,
          ...shared,
          ...langFields,
        });
      });
      return Promise.all(requests);
    },
    {
      onSuccess: () => {
        onClose();
        enqueueSnackbar(t("UPDATE_SUCCESSFULLY"), { variant: "success" });
        queryClient.invalidateQueries({ queryKey: [queryKeys.PRODUCTS] });
      },
      onError: () => {
        enqueueSnackbar(t("ERROR_DEFAULT"), { variant: "error" });
      },
    }
  );

  const { mutate: handleDelete, isLoading: isDeleting } = useMutation(
    () => {
      const requests = (productVariants || []).map((v) => deleteProduct(v.id));
      return Promise.all(requests);
    },
    {
      onSuccess: () => {
        setDeleteModalOpen(false);
        onClose();
        enqueueSnackbar(t("DELETED_SUCCESSFULLY"), { variant: "success" });
        queryClient.invalidateQueries({ queryKey: [queryKeys.PRODUCTS] });
      },
      onError: () => {
        enqueueSnackbar(t("ERROR_DEFAULT"), { variant: "error" });
      },
    }
  );

  return (
    <>
      <Modal
        onClose={onClose}
        open={open}
        title={t("EDIT_PRODUCT")}
        fullWidth
        maxWidth="md"
      >
        <ProductForm
          handleSubmit={(values) => handleEdit(values)}
          isLoading={isEditing}
          buttonText={t("SAVE")}
          productVariants={productVariants}
          mode="edit"
        />
        <Box px={2} pb={2}>
          <Button
            color="error"
            variant="outlined"
            fullWidth
            onClick={() => setDeleteModalOpen(true)}
            disabled={isEditing}
          >
            {t("DELETE")}
          </Button>
        </Box>
      </Modal>
      <DeleteConfirmationModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        handleDelete={handleDelete}
        isLoading={isDeleting}
        title={t("DELETE_PRODUCT")}
        extraInfo={t("DELETE_PRODUCT_CONFIRMATION")}
      />
    </>
  );
};

export default EditProductModal;
